import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomPagination from "../../common/custompagination";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import { formateDateWithMonth } from "../../../../utils/commonfunction";
import CustomRangepicker from "../../common/rangepicker";
import { PaymentTransactionLogsList } from "../../services/paymentTransaction.services";

const PaymentTransactionList = () => {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const [list, setList] = useState([]);
  const [datalength, setDataLength] = useState("");
  const [itemperpage] = useState(10);
  const [sorting] = useState({});
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [resetdate, setResetDate] = useState(false);
  const [globalsearch, setGlobalSearch] = useState("");
  const [search, setSearch] = useState({});

  const breadcrumbs = [
    { title: t("contact-us.link_dashboard"), url: "/admin/dashboard" },
    { title: "Transactions", url: "" },
  ];

  useEffect(() => {
    const getData = setTimeout(() => {
      if (search) {
        setLoader(true);
        const formData = new FormData();
        formData.append("page", params.pgno || 1);
        formData.append("per_page", itemperpage);
        formData.append("sort", JSON.stringify(sorting));
        formData.append("search", JSON.stringify(search));
        formData.append("global_search", globalsearch);

        PaymentTransactionLogsList(formData)
          .then((data) => {
            setDataLength(data.data.total_records);
            setList(data && data.data && data.data.list ? data?.data?.list : []);
            setPage(data && data.data && data.data.page ? data.data.page : 1);
            setLoader(false);
          })
          .catch((error) => {
            setTimeout(() => {
              setLoader(false);
            }, 500);
            console.log("error ====> ", error);
          });
      }
    }, 300);
    return () => clearTimeout(getData);
  }, [location, sorting, search, globalsearch, itemperpage, params.pgno]);

  const prepareSearch = (key, value) => {
    let sr = { ...search };
    if (String(value).length > 0) {
      sr[key] = value;
    } else {
      delete sr[key];
    }
    setSearch(sr);
  };

  const resetFilter = (e) => {
    e.preventDefault();
    setGlobalSearch("");
    prepareSearch();
    setSearch({});
    setResetDate(!resetdate);
  };

  const getPlatformBadge = (platform) => {
    const badges = {
      apple: "bg-success",
      android: "bg-info",
      manual: "bg-warning",
      other: "bg-secondary",
    };
    return (
      <span className={`badge ${badges[platform] || "bg-secondary"} fs-10`}>
        {platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : "Other"}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "bg-warning",
      completed: "bg-success",
      failed: "bg-danger",
      refunded: "bg-danger",
    };
    return (
      <span className={`badge ${badges[status] || "bg-secondary"} fs-10`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown"}
      </span>
    );
  };

  const getTransactionTypeBadge = (type) => {
    const badges = {
      purchase: "bg-primary",
      webhook: "bg-info",
      manual_activation: "bg-warning",
    };
    return (
      <span className={`badge ${badges[type] || "bg-secondary"} fs-10`}>
        {type ? type.replace(/_/g, " ").charAt(0).toUpperCase() + type.replace(/_/g, " ").slice(1) : "Other"}
      </span>
    );
  };

  const getNotificationTypeBadge = (type) => {
    const badges = {
      // Apple
      SUBSCRIBED: "bg-primary",
      DID_RENEW: "bg-info",
      EXPIRED: "bg-danger",
      DID_FAIL_TO_RENEW: "bg-warning",
      REFUND: "bg-danger",
      REVOKE: "bg-danger",
      // Google
      SUBSCRIPTION_RECOVERED: "bg-primary",
      SUBSCRIPTION_RENEWED: "bg-info",
      SUBSCRIPTION_CANCELED: "bg-danger",
      SUBSCRIPTION_PURCHASED: "bg-success",
      SUBSCRIPTION_ON_HOLD: "bg-warning",
      SUBSCRIPTION_IN_GRACE_PERIOD: "bg-info",
      SUBSCRIPTION_RESTARTED: "bg-primary",
      SUBSCRIPTION_PRICE_CHANGE_CONFIRMED: "bg-info",
      SUBSCRIPTION_DEFERRED: "bg-warning",
      SUBSCRIPTION_PAUSED: "bg-warning",
      SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED: "bg-info",
      SUBSCRIPTION_REVOKED: "bg-danger",
      SUBSCRIPTION_EXPIRED: "bg-danger",
    };
    return (
      <span className={`badge ${badges[type] || "bg-secondary"} fs-10`}>
        {type ? type.replace(/_/g, " ").charAt(0).toUpperCase() + type.replace(/_/g, " ").slice(1) : "N/A"}
      </span>
    );
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6 className="main-content-label">Payment Transactions</h6>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <div className="form-group mb-0 me-3">
                  <div className="form-group mb-0 rangepicker_container filter_design">
                    <i className="fa fa-search calender_icon"></i>
                    <input
                      type="search"
                      className="form-control"
                      value={globalsearch}
                      placeholder="Search by user, transaction ID, etc."
                      onChange={(e) => {
                        setGlobalSearch(e.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="form-group mb-0 me-3">
                  <select
                    className="form-control"
                    value={search.platform || ""}
                    onChange={(e) => prepareSearch("platform", e.target.value)}
                  >
                    <option value="">All Platforms</option>
                    <option value="apple">Apple</option>
                    <option value="android">Android</option>
                    <option value="manual">Manual</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group mb-0 me-3">
                  <select
                    className="form-control"
                    value={search.transaction_type || ""}
                    onChange={(e) => prepareSearch("transaction_type", e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="purchase">Purchase</option>
                    <option value="webhook">Webhook</option>
                    <option value="manual_activation">Manual Activation</option>
                  </select>
                </div>

                <div className="form-group mb-0 me-3">
                  <select
                    className="form-control"
                    value={search.notification_type || ""}
                    onChange={(e) => prepareSearch("notification_type", e.target.value)}
                  >
                    <option value="">All Notifications</option>
                    {/* Apple */}
                    <option value="SUBSCRIBED">Apple - Subscribed</option>
                    <option value="DID_RENEW">Apple - Did Renew</option>
                    <option value="EXPIRED">Apple - Expired</option>
                    <option value="DID_FAIL_TO_RENEW">Apple - Did Fail To Renew</option>
                    <option value="REFUND">Apple - Refund</option>
                    <option value="REVOKE">Apple - Revoke</option>
                    {/* Google */}
                    <option value="SUBSCRIPTION_RECOVERED">Google - Recovered</option>
                    <option value="SUBSCRIPTION_RENEWED">Google - Renewed</option>
                    <option value="SUBSCRIPTION_CANCELED">Google - Canceled</option>
                    <option value="SUBSCRIPTION_PURCHASED">Google - Purchased</option>
                    <option value="SUBSCRIPTION_ON_HOLD">Google - On Hold</option>
                    <option value="SUBSCRIPTION_IN_GRACE_PERIOD">Google - Grace Period</option>
                    <option value="SUBSCRIPTION_RESTARTED">Google - Restarted</option>
                    <option value="SUBSCRIPTION_PRICE_CHANGE_CONFIRMED">Google - Price Change Confirmed</option>
                    <option value="SUBSCRIPTION_DEFERRED">Google - Deferred</option>
                    <option value="SUBSCRIPTION_PAUSED">Google - Paused</option>
                    <option value="SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED">Google - Pause Schedule Changed</option>
                    <option value="SUBSCRIPTION_REVOKED">Google - Revoked</option>
                    <option value="SUBSCRIPTION_EXPIRED">Google - Expired</option>
                  </select>
                </div>

                <div className="form-group mb-0 me-3">
                  <select
                    className="form-control"
                    value={search.status || ""}
                    onChange={(e) => prepareSearch("status", e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>

                <div className="me-3">
                  <CustomRangepicker
                    GetDateRange={(e) => {
                      prepareSearch("createdAt", e);
                    }}
                    resetdate={resetdate}
                  />
                </div>
                <button
                  type="reset"
                  value="Reset"
                  onClick={resetFilter}
                  className="btn btn-warning float-right mr-2"
                >
                  Reset Filter
                </button>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered border-t0 key-buttons text-nowrap w-100">
                <thead>
                  <tr>
                    <th className="position-relative select_head">
                      <span>S.No</span>
                    </th>
                    <th>
                      <div className="sorting_column">
                        <span>User</span>
                      </div>
                    </th>
                    <th>
                      <div className="rating_head">
                        <span>Platform</span>
                      </div>
                    </th>
                    <th>
                      <div className="rating_head">
                        <span>Original Transaction ID</span>
                      </div>
                    </th>
                    <th>
                      <div>
                        <span>Product</span>
                      </div>
                    </th>
                    <th>
                      <div>
                        <span>Amount</span>
                      </div>
                    </th>
                    <th>
                      <div className="text-center">
                        <span>Event</span>
                      </div>
                    </th>
                    <th className="created_head">
                      <div className="sorting_column">
                        <span>Created At</span>
                      </div>
                    </th>
                    <th className="created_head">
                      <div className="sorting_column">
                        <span>End Date</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={10}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    <>
                      {list.length ? (
                        list.map((row, i) => {
                          return (
                            <tr key={i}>
                              <td>
                                {row ? ((page - 1) * itemperpage) + i + 1 : null}
                              </td>
                              <td>{row?.user_name || "N/A"}</td>
                              <td>{getPlatformBadge(row?.platform)}</td>
                              <td className="fw-bold text-primary">
                                {row?.display_transaction_id || row?.original_transaction_id || row?.order_id || row?.transaction_id || row?.purchase_token || "N/A"}
                              </td>
                              <td>{row?.product_id || row?.plan_name || "N/A"}</td>
                              <td>
                                {(row?.amount) != null
                                  ? `${row?.currency || 'USD'} ${Number(row?.amount).toFixed(2)}`
                                  : "N/A"}
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-2 justify-content-center">
                                  <div className="d-flex flex-column gap-1 align-items-center">
                                    <div className="d-flex gap-1 align-items-center">
                                      {getNotificationTypeBadge(row?.notification_type)}
                                      {getTransactionTypeBadge(row?.transaction_type)}
                                      {getStatusBadge(row?.status)}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {row.createdAt ? formateDateWithMonth(row.createdAt) : "N/A"}
                              </td>
                              <td>
                                {row.end_date ? formateDateWithMonth(row.end_date) : "N/A"}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center">
                            No records found
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
            <div className="" id="example1_info" role="status" aria-live="polite">
              <b>Total Records : {datalength ? datalength : "0"}</b>
            </div>
            {datalength && datalength > 0 ? (
              <CustomPagination
                datalength={datalength}
                itemperpage={itemperpage}
                currentPage={page}
                setPage={setPage}
                pageRoute={[
                  {
                    name: "Payment Transactions",
                    path: "/admin/subscription/payment-transactions/list",
                  },
                ]}
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentTransactionList;
